import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useForm } from "react-hook-form";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { getDevice } from "./utils/deviceDetector";
import {
  saveApplicationScreen3,
  saveApplicationScreen4,
  saveApplicationScreen5,
  saveBirthdayCellphoneAndAddress,
  saveFirstAndLastName,
  submitApplication,
  uploadFile,
} from "./utils/firebaseUtils";
import { useNavigate } from "react-router";
import "./account.css";

const Account = () => {
  const { profile, application, loading } = useAuth();
  const deviceType = useMemo(() => getDevice(), []); // Compute once and cache
  const [currentScreen, setCurrentScreen] = useState(1); // Track the current step

  // save and exit | save and continue btns
  const [loadingSaveAndContinue, setLoadingSaveAndContinue] = useState(false);
  const [loadingSaveAndExit, setLoadingSaveAndExit] = useState(false);

  // Track uploaded documents
  const [uploadedDocs, setUploadedDocs] = useState({
    writtenStatementUrl: null,
    collegeAcceptanceLetterUrl: null,
    letter1Url: null,
    letter2Url: null,
    communityServiceUrl: null,
    schoolIdUrl: null,
    transcriptUrl: null,
  });

  // Track individual upload progress
  const [uploadingDoc, setUploadingDoc] = useState(null);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({});

  const previousWinner = watch("previousWinner");
  const alphaMentor = watch("alphaMentor");
  const alphaFamilyMember = watch("alphaFamilyMember");

  const scholarshipUploadRequirements = [
    {
      title: "Personal Statement",
      description: (
        <>
          <p>
            Discuss an experience that had a profound impact on your life.
            Discuss how this scholarship might impact your education
            aspirations, and/or career pursuits. Please share information
            demonstrating where you have shown leadership, significant
            involvement, or active participation in community service, including
            clubs, organizations, or meaningful service to others on or off your
            college campus. Your statement should be well written, typed, and
            concise.
          </p>
        </>
      ),
    },
    {
      title: "Two recent letters of recommendation",
      description: (
        <>
          <p>
            letters must be signed and dated on letterhead with sender's contact
            information. Your name must be written on the letters of
            recommendation.
          </p>
        </>
      ),
    },
    {
      title: "Community Service",
      description: (
        <>
          <p>
            A signed and dated certificate or letter on letterhead that credits
            you with a community service activity.
          </p>
        </>
      ),
    },
    {
      title: "Transcript",
      description: (
        <>
          <p>
            Official transcripts (certified by School Official and/or Registrar)
            - Copies are not acceptable. Must show GPA on a 4.0 scale. Grades
            must be converted to a 4.0 scale prior to the submission of your
            application. (Contact your school Counselor or Registrar to have
            electronic transcript processed and sent to:
            rho1914scholarships@gmail.com)
          </p>
        </>
      ),
    },
    {
      title: "Student ID",
      description: (
        <>
          <p>Copy of current student ID or photo.</p>
        </>
      ),
    },
  ];

  const getHeading = useCallback(() => {
    if (currentScreen === 1) return <h2>Finish Setting up your Profile</h2>;
    if (currentScreen === 2 || currentScreen === 3)
      return <h2>Personal Information</h2>;
    if (currentScreen === 4) return <h2>Academic Information</h2>;
    if (currentScreen === 5)
      return (
        <h2 style={{ fontSize: "1.5rem" }}>
          Career Goals & Community Involvement
        </h2>
      );
    if (currentScreen === 6 || currentScreen === 7)
      return <h2>Upload Documents</h2>;
  }, [currentScreen]);

  const onSubmit = async (data) => {
    return;
  };

  const validateFile = (file) => {
    if (!file) return "File is required";
    if (file.size > 10 * 1024 * 1024)
      return "File size must be less than 10 MB";
    if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type)) {
      return "File must be a PDF or image (JPEG/PNG)";
    }
    return true;
  };

  // Check if all required documents are uploaded
  const canSubmit = () => {
    const required = [
      uploadedDocs.writtenStatementUrl,
      uploadedDocs.communityServiceUrl,
      uploadedDocs.schoolIdUrl,
      uploadedDocs.transcriptUrl,
    ];

    if (application?.applicantLevel === "High School Senior") {
      required.push(uploadedDocs.collegeAcceptanceLetterUrl);
    }

    // Letters of rec are required if NOT a previous winner
    if (application?.previousWinner !== "yes") {
      required.push(uploadedDocs.letter1Url);
      required.push(uploadedDocs.letter2Url);
    }

    return required.every((url) => url !== null && url !== "");
  };

  // Handle individual document upload
  const handleDocumentUpload = async (file, fieldName) => {
    if (!file) return;

    const validation = validateFile(file);
    if (validation !== true) {
      alert(validation);
      return;
    }

    setUploadingDoc(fieldName);
    try {
      const downloadURL = await uploadFile(file, fieldName, profile);

      // Update local state
      setUploadedDocs((prev) => ({
        ...prev,
        [`${fieldName}Url`]: downloadURL,
      }));

      // Save to Firestore immediately
      await submitApplication({ [`${fieldName}Url`]: downloadURL }, profile);

      alert(`${fieldName} uploaded successfully!`);
    } catch (error) {
      console.error("Error uploading document:", error);
      alert("Failed to upload document. Please try again.");
    } finally {
      setUploadingDoc(null);
    }
  };

  // Handle final submission
  const handleFinalSubmit = async () => {
    if (!canSubmit()) {
      alert("Please upload all required documents before submitting.");
      return;
    }

    setLoadingSaveAndContinue(true);
    try {
      await submitApplication({ submitted: true }, profile);
      alert("Application submitted successfully!");
    } catch (error) {
      console.error("Error submitting application:", error);
      alert("Failed to submit application. Please try again.");
    } finally {
      setLoadingSaveAndContinue(false);
    }
  };

  // Function to log form data and proceed to the next step
  const handleNextStep = async (data) => {
    setLoadingSaveAndContinue(true);

    const trimmedData = {
      firstName:
        data?.firstName?.trim().charAt(0).toUpperCase() +
        data?.firstName?.trim().slice(1),
      lastName:
        data?.lastName?.trim().charAt(0).toUpperCase() +
        data?.lastName?.trim().slice(1),
    };

    if (currentScreen === 1) {
      const result = await saveFirstAndLastName(
        trimmedData.firstName,
        trimmedData.lastName,
      );
      setLoadingSaveAndContinue(false);

      if (!result.success) {
        setError("root", { message: "Failed to save name" });
        return;
      }
      setCurrentScreen(2);
    }

    if (currentScreen === 2) {
      const result = await saveBirthdayCellphoneAndAddress(
        data?.birthday,
        data?.cellphone,
        data?.address,
      );
      setLoadingSaveAndContinue(false);

      if (!result.success) {
        setError("root", { message: "Failed to save" });
        return;
      }
      setCurrentScreen(3);
    }

    if (currentScreen === 3) {
      const result = await saveApplicationScreen3(data, profile);
      setLoadingSaveAndContinue(false);
      if (!result.success) {
        setError("root", { message: result.message });
        return;
      }
      setCurrentScreen(4);
    }

    if (currentScreen === 4) {
      const result = await saveApplicationScreen4(data, profile);
      setLoadingSaveAndContinue(false);
      if (!result.success) {
        setError("root", { message: result.message });
        return;
      }
      setCurrentScreen(5);
    }

    if (currentScreen === 5) {
      const result = await saveApplicationScreen5(data, profile);
      setLoadingSaveAndContinue(false);
      if (!result.success) {
        setError("root", { message: result.message });
        return;
      }
      setCurrentScreen(6);
    }

    if (currentScreen === 6) {
      setLoadingSaveAndContinue(false);
      setCurrentScreen(7);
    }

    if (currentScreen === 7) {
      await handleFinalSubmit();
    }
  };

  // Function to save and exit
  const handleSaveAndExit = async (data) => {
    setLoadingSaveAndExit(true);

    const trimmedData = {
      firstName:
        data?.firstName?.trim().charAt(0).toUpperCase() +
        data?.firstName?.trim().slice(1),
      lastName:
        data?.lastName?.trim().charAt(0).toUpperCase() +
        data?.lastName?.trim().slice(1),
    };

    if (currentScreen === 1) {
      const result = await saveFirstAndLastName(
        trimmedData.firstName,
        trimmedData.lastName,
      );
      setLoadingSaveAndExit(false);

      if (!result.success) {
        setError("root", { message: "Failed to save name" });
        return;
      }
    }

    if (currentScreen === 2) {
      const result = await saveBirthdayCellphoneAndAddress(
        data?.birthday,
        data?.cellphone,
        data?.address,
      );
      setLoadingSaveAndExit(false);

      if (!result.success) {
        setError("root", { message: "Failed to save" });
        return;
      }
    }

    if (currentScreen === 3) {
      const result = await saveApplicationScreen3(data, profile);
      setLoadingSaveAndExit(false);

      if (!result.success) {
        setError("root", { message: "Failed to save" });
        return;
      }
    }

    if (currentScreen === 4) {
      const result = await saveApplicationScreen4(data, profile);
      setLoadingSaveAndExit(false);

      if (!result.success) {
        setError("root", { message: "Failed to save" });
        return;
      }
    }

    if (currentScreen === 5) {
      const result = await saveApplicationScreen5(data, profile);
      setLoadingSaveAndExit(false);
      if (!result.success) {
        setError("root", { message: "Failed to save" });
        return;
      }
    }

    navigate("/", { replace: true });
  };

  // Load existing uploaded documents when reaching screen 7
  useEffect(() => {
    if (currentScreen === 7 && application) {
      setUploadedDocs({
        writtenStatementUrl: application?.writtenStatementUrl || null,
        collegeAcceptanceLetterUrl:
          application?.collegeAcceptanceLetterUrl || null,
        letter1Url: application?.letter1Url || null,
        letter2Url: application?.letter2Url || null,
        communityServiceUrl: application?.communityServiceUrl || null,
        schoolIdUrl: application?.schoolIdUrl || null,
        transcriptUrl: application?.transcriptUrl || null,
      });
    }
  }, [currentScreen, application]);

  // set default values
  useEffect(() => {
    if (profile) {
      reset({
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        birthday: profile?.birthday || new Date().toISOString().split("T")[0],
        cellphone: profile?.cellphone,
        address: profile?.address,
        applicantLevel: application?.applicantLevel || "",
        previousWinner: application?.previousWinner,
        yearReceived: application?.yearReceived,
        alphaFamilyMember: application?.alphaFamilyMember,
        familyMemberName: application?.familyMemberName,
        familyMemberChapter: application?.familyMemberChapter,
        alphaMentor: application?.alphaMentor,
        mentorName: application?.mentorName,
        schoolName: application?.schoolName,
        schoolAddress: application?.schoolAddress,
        gpa: application?.gpa,
        major: application?.major,
        minor: application?.minor,
        collegesAccepted: application?.collegesAccepted,
        collegesPending: application?.collegesPending,
        academicClassification: application?.academicClassification,
        careerGoals: application?.careerGoals,
        involvement: application?.involvement,
      });
    }
  }, [profile, application, reset]);

  useEffect(() => {
    if (profile?.firstName && profile?.lastName && currentScreen === 1) {
      setCurrentScreen(2);
    }
  }, [currentScreen, profile]);

  useEffect(() => {
    if (application?.submitted) {
      navigate("/thank-you", { replace: true });
    }
  }, [application, navigate]);

  useEffect(() => {
    if (profile?.roles?.includes("reviewer")) {
      navigate("/submission-dashboard", { replace: true });
    }
  }, [profile, navigate]);

  if (loading) {
    return (
      <div
        style={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span className="page-loader"></span>
      </div>
    );
  }

  // Get missing required documents
  const getMissingDocs = () => {
    const missing = [];
    if (!uploadedDocs.writtenStatementUrl) missing.push("Personal Statement");
    if (!uploadedDocs.communityServiceUrl)
      missing.push("Community Service Verification");
    if (!uploadedDocs.schoolIdUrl) missing.push("School ID");
    if (!uploadedDocs.transcriptUrl) missing.push("Official Transcript");
    if (
      application?.applicantLevel === "High School Senior" &&
      !uploadedDocs.collegeAcceptanceLetterUrl
    ) {
      missing.push("College Acceptance Letter");
    }

    // Letters of rec are required if NOT a previous winner
    if (application?.previousWinner !== "yes") {
      if (!uploadedDocs.letter1Url)
        missing.push("1st Letter of Recommendation");
      if (!uploadedDocs.letter2Url)
        missing.push("2nd Letter of Recommendation");
    }

    return missing;
  };

  // applicant view
  return (
    <div className="account-container">
      <div className="account-content">
        {getHeading()}
        <form
          className="scholarship-application-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          {currentScreen === 1 && (
            <>
              {/* first name */}
              <div className="input-group">
                <label htmlFor="first-name">First Name *</label>
                <input
                  {...register("firstName", {
                    required: "First name is required",
                    minLength: {
                      value: 2,
                      message: "Must be at least 2 characters",
                    },
                    validate: (value) =>
                      value.trim().length > 0 || "Cannot be only spaces",
                  })}
                  type="text"
                  id="first-name"
                  name="firstName"
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <p className="error-message">{errors.firstName.message}</p>
                )}
              </div>

              {/* last name */}
              <div className="input-group">
                <label htmlFor="last-name">Last Name *</label>
                <input
                  {...register("lastName", {
                    required: "Last name is required",
                    minLength: {
                      value: 2,
                      message: "Must be at least 2 characters",
                    },
                    validate: (value) =>
                      value.trim().length > 0 || "Cannot be only spaces",
                  })}
                  type="text"
                  id="last-name"
                  name="lastName"
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <p className="error-message">{errors.lastName.message}</p>
                )}
              </div>
            </>
          )}

          {currentScreen === 2 && (
            <>
              {/* Birthday */}
              <div className="input-group">
                <label htmlFor="birthday">Date of Birth (MM/DD/YYYY) *</label>
                <input
                  {...register("birthday", {
                    required: "Your date of birth is required",
                    validate: (value) => {
                      const selectedDate = new Date(value);
                      const today = new Date();
                      const minAgeDate = new Date();
                      minAgeDate.setFullYear(today.getFullYear() - 14);

                      if (selectedDate > today) {
                        return "Date of birth cannot be in the future";
                      }
                      if (selectedDate > minAgeDate) {
                        return "You must be at least 14 years old to continue";
                      }
                      return true;
                    },
                  })}
                  type="date"
                  id="birthday"
                  name="birthday"
                />
                {errors.birthday && (
                  <p className="error-message">{errors.birthday.message}</p>
                )}
              </div>

              {/* Cellphone Number */}
              <div className="input-group">
                <label htmlFor="cellphone">Cellphone Number *</label>
                <input
                  {...register("cellphone", {
                    required: "Cellphone number is required",
                    pattern: {
                      value: /^\+?\d+$/,
                      message: "Enter a valid phone number (numbers only)",
                    },
                    minLength: {
                      value: 6,
                      message: "Enter a valid phone number",
                    },
                    maxLength: {
                      value: 15,
                      message: "Enter a valid phone number",
                    },
                    setValueAs: (value) => value.replace(/[^\d+]/g, "").trim(),
                  })}
                  type="tel"
                  id="cellphone"
                  name="cellphone"
                  className="full-width-input"
                  placeholder="Enter your phone number"
                />
                {errors.cellphone && (
                  <p className="error-message">{errors.cellphone.message}</p>
                )}
              </div>

              {/* address */}
              <div className="input-group">
                <label htmlFor="address">Full Home Address *</label>
                <label>Include street, city, state, and zip code</label>
                <input
                  id="address"
                  {...register("address", {
                    required: "Address is required",
                    minLength: {
                      value: 5,
                      message: "Enter a valid address",
                    },
                    setValueAs: (value) => value.trim(),
                  })}
                  placeholder="Enter your full address"
                />
                {errors.address && <p>{errors.address.message}</p>}
              </div>
            </>
          )}

          {currentScreen === 3 && (
            <>
              {/* applicant level */}
              <div className="input-group select-input-group">
                <label style={{ fontSize: "20px" }} htmlFor="applicantLevel">
                  Applying as a current
                </label>
                <select
                  id="applicantLevel"
                  {...register("applicantLevel", {
                    required: "Please select an option",
                  })}
                  style={{ height: "25px", width: "250px" }}
                >
                  <option value="" disabled>
                    Select an option below
                  </option>
                  <option value="High School Senior">High School Senior</option>
                  <option value="College Student">College Student</option>
                  <option value="Current College Brother">
                    Current College Brother
                  </option>
                </select>
                {errors.applicantLevel && (
                  <p className="error-message">
                    {errors.applicantLevel.message}
                  </p>
                )}
              </div>

              {/* check if received scholarship before */}
              <div
                style={{
                  border: "1px solid #333",
                  borderRadius: "6px",
                  padding: "5px 10px",
                  margin: "10px 0px",
                }}
              >
                {!(application?.applicantLevel === "High School Senior") && (
                  <div className="radio-input-group">
                    <label className="radio-label">
                      Have you received a scholarship from Rho Chapter? *
                    </label>

                    <div className="radio-options">
                      <label>
                        <input
                          type="radio"
                          {...register("previousWinner", { required: true })}
                          value="yes"
                        />
                        <span
                          style={{
                            marginTop: deviceType === "iOS" ? "10px" : null,
                          }}
                        >
                          Yes
                        </span>
                      </label>
                      <label>
                        <input
                          type="radio"
                          {...register("previousWinner", { required: true })}
                          value="no"
                        />
                        <span
                          style={{
                            marginTop: deviceType === "iOS" ? "10px" : null,
                          }}
                        >
                          No
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {previousWinner === "yes" && (
                  <div style={{ width: "80%" }} className="input-group">
                    <label htmlFor="yearReceived">Year Received</label>
                    <input
                      type="number"
                      id="yearReceived"
                      {...register("yearReceived")}
                      placeholder="Enter year received"
                    />
                    {errors.yearReceived}
                  </div>
                )}
              </div>

              {/* check if has a mentor */}
              <div
                style={{
                  border: "1px solid #333",
                  borderRadius: "6px",
                  padding: "5px 10px",
                  margin: "10px 0px",
                }}
              >
                <div className="radio-input-group">
                  <label className="radio-label">
                    Do you have a mentor who is a member of Alpha Phi Alpha? *
                  </label>

                  <div className="radio-options">
                    <label>
                      <input
                        type="radio"
                        {...register("alphaMentor", { required: true })}
                        value="yes"
                      />
                      <span
                        style={{
                          marginTop: deviceType === "iOS" ? "10px" : null,
                        }}
                      >
                        Yes
                      </span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        {...register("alphaMentor", { required: true })}
                        value="no"
                      />
                      <span
                        style={{
                          marginTop: deviceType === "iOS" ? "10px" : null,
                        }}
                      >
                        No
                      </span>
                    </label>
                  </div>
                </div>

                {alphaMentor === "yes" && (
                  <div style={{ width: "80%" }} className="input-group">
                    <label htmlFor="mentorName">Mentor's Name</label>
                    <input
                      type="text"
                      id="mentorName"
                      {...register("mentorName")}
                      placeholder="Enter mentor's name"
                    />
                    {errors.mentorName}
                  </div>
                )}
              </div>

              {/* check if has a family member in chapter */}
              <div
                style={{
                  border: "1px solid #333",
                  borderRadius: "6px",
                  padding: "5px 10px",
                  margin: "10px 0px",
                }}
              >
                <div className="radio-input-group">
                  <label className="radio-label">
                    Do you have a family member who is a member of Alpha Phi
                    Alpha? *
                  </label>

                  <div className="radio-options">
                    <label>
                      <input
                        type="radio"
                        {...register("alphaFamilyMember", { required: true })}
                        value="yes"
                      />
                      <span
                        style={{
                          marginTop: deviceType === "iOS" ? "10px" : null,
                        }}
                      >
                        Yes
                      </span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        {...register("alphaFamilyMember", { required: true })}
                        value="no"
                      />
                      <span
                        style={{
                          marginTop: deviceType === "iOS" ? "10px" : null,
                        }}
                      >
                        No
                      </span>
                    </label>
                  </div>
                </div>

                {alphaFamilyMember === "yes" && (
                  <>
                    <div style={{ width: "80%" }} className="input-group">
                      <label htmlFor="familyMemberName">
                        Family Member's Name
                      </label>
                      <input
                        type="text"
                        id="familyMemberName"
                        {...register("familyMemberName")}
                        placeholder="Enter member's name"
                      />
                      {errors.familyMemberName}
                    </div>

                    <div style={{ width: "80%" }} className="input-group">
                      <label htmlFor="familyMemberChapter">
                        Chapter of Initiation
                      </label>
                      <input
                        type="text"
                        id="familyMemberChapter"
                        {...register("familyMemberChapter")}
                        placeholder="Chapter of initiation"
                      />
                      {errors.familyMemberChapter}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {currentScreen === 4 && (
            <>
              {/* School Name */}
              <div className="input-group">
                <label htmlFor="schoolName">School Name *</label>
                <input
                  type="text"
                  id="schoolName"
                  {...register("schoolName", {
                    required: "School name is required",
                  })}
                  placeholder="Enter your school name"
                />
                {errors.schoolName && (
                  <p className="error-message">{errors.schoolName.message}</p>
                )}
              </div>

              {/* Full School Address */}
              <div className="input-group">
                <label htmlFor="schoolAddress">Full School Address *</label>
                <label>Include street, city, state, and zip code</label>
                <p style={{ color: "#333", fontFamily: "gotham-light" }}>
                  If unsure, verify with Google
                </p>
                <input
                  type="text"
                  id="schoolAddress"
                  {...register("schoolAddress", {
                    required: "School address is required",
                  })}
                  placeholder="Enter your full school address"
                />
                {errors.schoolAddress && (
                  <p className="error-message">
                    {errors.schoolAddress.message}
                  </p>
                )}
              </div>

              {/* GPA */}
              <div className="input-group">
                <label htmlFor="gpa">GPA (Grade Point Average) Min 2.5 *</label>
                <input
                  type="number"
                  step="0.01"
                  id="gpa"
                  {...register("gpa", {
                    required: "GPA is required",
                    min: { value: 0, message: "GPA must be at least 2.5" },
                    max: { value: 4, message: "GPA cannot be more than 4.0" },
                  })}
                  placeholder="Enter your GPA"
                />
                {errors.gpa && (
                  <p className="error-message">{errors.gpa.message}</p>
                )}
              </div>

              {!(application?.applicantLevel === "High School Senior") && (
                <>
                  {/* Major Field of Study */}
                  <div className="input-group">
                    <label htmlFor="major">Major Field of Study *</label>
                    <input
                      id="major"
                      {...register("major", {
                        required: !(
                          application?.applicantLevel === "High School Senior"
                        )
                          ? "Major is required"
                          : false,
                      })}
                      placeholder="Enter your major"
                    />
                    {errors.major && (
                      <p className="error-message">{errors.major.message}</p>
                    )}
                  </div>

                  {/* Minor (Optional) */}
                  <div className="input-group">
                    <label htmlFor="minor">Minor (if applicable)</label>
                    <input
                      id="minor"
                      {...register("minor")}
                      placeholder="Enter your minor (if any)"
                    />
                  </div>

                  {/* Current Academic Classification */}
                  <div className="input-group select-input-group">
                    <label htmlFor="academicClassification">
                      Current Academic Classification *
                    </label>
                    <select
                      id="academicClassification"
                      {...register("academicClassification", {
                        required: !(
                          application?.applicantLevel === "High School Senior"
                        )
                          ? "Academic classification is required"
                          : false,
                      })}
                      style={{ height: "25px", width: "250px" }}
                    >
                      <option value="">Select your classification</option>
                      <option value="Freshman">Freshman</option>
                      <option value="Sophomore">Sophomore</option>
                      <option value="Junior">Junior</option>
                      <option value="Senior">Senior</option>
                      <option value="Graduate Student">Graduate Student</option>
                    </select>
                    {errors.academicClassification && (
                      <p className="error-message">
                        {errors.academicClassification.message}
                      </p>
                    )}
                  </div>
                </>
              )}

              {application?.applicantLevel === "High School Senior" && (
                <>
                  {/* Colleges Accepted To */}
                  <div className="input-group">
                    <label htmlFor="collegesAccepted">
                      Colleges You've Been Accepted To *
                    </label>
                    <textarea
                      id="collegesAccepted"
                      {...register("collegesAccepted", {
                        required:
                          application?.applicantLevel === "High School Senior"
                            ? "This field is required"
                            : false,
                      })}
                      placeholder="List the colleges you've been accepted to"
                      rows={5}
                    />
                    {errors.collegesAccepted && (
                      <p className="error-message">
                        {errors.collegesAccepted.message}
                      </p>
                    )}
                  </div>

                  {/* Colleges Pending Acceptance */}
                  <div className="input-group">
                    <label htmlFor="collegesPending">
                      Colleges You're Waiting to Hear From *
                    </label>
                    <textarea
                      id="collegesPending"
                      {...register("collegesPending", {
                        required:
                          application?.applicantLevel === "High School Senior"
                            ? "This field is required"
                            : false,
                      })}
                      placeholder="List the colleges you're waiting to hear from"
                      rows={5}
                    />
                    {errors.collegesPending && (
                      <p className="error-message">
                        {errors.collegesPending.message}
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {currentScreen === 5 && (
            <>
              {/* career goals */}
              <div className="input-group">
                {!(application?.applicantLevel === "High School Senior") ? (
                  <label htmlFor="careerGoals">List your career goals</label>
                ) : (
                  <label htmlFor="careerGoals">
                    List your Post-Graduate or career goals
                  </label>
                )}
                <textarea
                  id="careerGoals"
                  {...register("careerGoals", {
                    required: "This field is required",
                  })}
                  placeholder="Enter goals here"
                  rows={5}
                />
                {errors.careerGoals && (
                  <p className="error-message">{errors.careerGoals.message}</p>
                )}
              </div>

              {/* community/school involvement */}
              <div className="input-group">
                {!(application?.applicantLevel === "High School Senior") ? (
                  <label htmlFor="involvement">
                    List community or school involvement
                  </label>
                ) : (
                  <>
                    <label htmlFor="involvement">
                      List your community service, leadership
                    </label>
                    <label htmlFor="involvement">
                      and/or school involvement activities
                    </label>
                  </>
                )}

                <textarea
                  id="involvement"
                  {...register("involvement", {
                    required: "This field is required",
                  })}
                  placeholder="Enter involvement here"
                  rows={5}
                />
                {errors.involvement && (
                  <p className="error-message">{errors.involvement.message}</p>
                )}
              </div>
            </>
          )}

          {currentScreen === 6 && (
            <>
              <div className="upload-doc-statement">
                <p>
                  To ensure that your application is ready to submit, make sure
                  that you review and complete all of the required tasks on this
                  checklist.
                </p>
              </div>
              {/* Requirements Section */}
              <div className="scholarshipUploadRequirements-section">
                {scholarshipUploadRequirements.map(({ title, description }) => (
                  <div
                    key={title}
                    className="scholarshipUploadRequirements-card"
                  >
                    <h2>{title}</h2>
                    <span>{description}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {currentScreen === 7 && (
            <>
              <div className="upload-progress-section">
                <h3>Document Upload Status</h3>
                {getMissingDocs().length > 0 ? (
                  <div className="missing-required-docs">
                    <p style={{ color: "#d32f2f", fontWeight: "bold" }}>
                      Missing Required Documents:
                    </p>
                    <ul>
                      {getMissingDocs().map((doc) => (
                        <li key={doc}>{doc}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p style={{ color: "#2e7d32", fontWeight: "bold" }}>
                    ✓ All required documents uploaded!
                  </p>
                )}
              </div>

              {/* Written Statement (Required) */}
              <div className="document-upload-item">
                <div className="document-info">
                  <label>Personal Statement * </label>
                  {uploadedDocs.writtenStatementUrl && (
                    <span className="uploaded-indicator">✓ Uploaded</span>
                  )}
                </div>
                <input
                  type="file"
                  id="writtenStatement"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleDocumentUpload(file, "writtenStatement");
                  }}
                  disabled={uploadingDoc === "writtenStatement"}
                />
                {uploadingDoc === "writtenStatement" && (
                  <p className="uploading-text">Uploading...</p>
                )}
              </div>

              {/* College Acceptance Letter (Required for HS Seniors) */}
              {application?.applicantLevel === "High School Senior" && (
                <div className="document-upload-item">
                  <div className="document-info">
                    <label>College Acceptance Letter *</label>
                    {uploadedDocs.collegeAcceptanceLetterUrl && (
                      <span className="uploaded-indicator">✓ Uploaded</span>
                    )}
                  </div>
                  <input
                    type="file"
                    id="collegeAcceptanceLetter"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file)
                        handleDocumentUpload(file, "collegeAcceptanceLetter");
                    }}
                    disabled={uploadingDoc === "collegeAcceptanceLetter"}
                  />
                  {uploadingDoc === "collegeAcceptanceLetter" && (
                    <p className="uploading-text">Uploading...</p>
                  )}
                </div>
              )}

              {/* 1st Letter of Recommendation */}
              <div className="document-upload-item">
                <div className="document-info">
                  <label>
                    1st Letter of Recommendation{" "}
                    {application?.previousWinner !== "yes" ? "*" : "(Optional)"}
                  </label>
                  {uploadedDocs.letter1Url && (
                    <span className="uploaded-indicator">✓ Uploaded</span>
                  )}
                </div>
                <input
                  type="file"
                  id="letter1"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleDocumentUpload(file, "letter1");
                  }}
                  disabled={uploadingDoc === "letter1"}
                />
                {uploadingDoc === "letter1" && (
                  <p className="uploading-text">Uploading...</p>
                )}
              </div>

              {/* 2nd Letter of Recommendation */}
              <div className="document-upload-item">
                <div className="document-info">
                  <label>
                    2nd Letter of Recommendation{" "}
                    {application?.previousWinner !== "yes" ? "*" : "(Optional)"}
                  </label>
                  {uploadedDocs.letter2Url && (
                    <span className="uploaded-indicator">✓ Uploaded</span>
                  )}
                </div>
                <input
                  type="file"
                  id="letter2"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleDocumentUpload(file, "letter2");
                  }}
                  disabled={uploadingDoc === "letter2"}
                />
                {uploadingDoc === "letter2" && (
                  <p className="uploading-text">Uploading...</p>
                )}
              </div>

              {/* Community Service Verification (Required) */}
              <div className="document-upload-item">
                <div className="document-info">
                  <label>Community Service Verification *</label>
                  {uploadedDocs.communityServiceUrl && (
                    <span className="uploaded-indicator">✓ Uploaded</span>
                  )}
                </div>
                <input
                  type="file"
                  id="communityService"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleDocumentUpload(file, "communityService");
                  }}
                  disabled={uploadingDoc === "communityService"}
                />
                {uploadingDoc === "communityService" && (
                  <p className="uploading-text">Uploading...</p>
                )}
              </div>

              {/* Current School ID (Required) */}
              <div className="document-upload-item">
                <div className="document-info">
                  <label>Current School ID *</label>
                  {uploadedDocs.schoolIdUrl && (
                    <span className="uploaded-indicator">✓ Uploaded</span>
                  )}
                </div>
                <input
                  type="file"
                  id="schoolId"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleDocumentUpload(file, "schoolId");
                  }}
                  disabled={uploadingDoc === "schoolId"}
                />
                {uploadingDoc === "schoolId" && (
                  <p className="uploading-text">Uploading...</p>
                )}
              </div>

              {/* Official Transcript (Required) */}
              <div className="document-upload-item">
                <div className="document-info">
                  <label>Official Transcript *</label>
                  {uploadedDocs.transcriptUrl && (
                    <span className="uploaded-indicator">✓ Uploaded</span>
                  )}
                </div>
                <input
                  type="file"
                  id="transcript"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleDocumentUpload(file, "transcript");
                  }}
                  disabled={uploadingDoc === "transcript"}
                />
                {uploadingDoc === "transcript" && (
                  <p className="uploading-text">Uploading...</p>
                )}
              </div>
            </>
          )}

          {errors.root && (
            <p className="error-message">{errors?.root?.message}</p>
          )}

          <div className="save-continue-btns">
            {currentScreen > 1 && (
              <span
                style={{
                  paddingTop: deviceType === "iOS" ? "25px" : "auto",
                  lineHeight: deviceType === "iOS" ? 0.8 : "auto",
                }}
                onClick={() =>
                  setCurrentScreen((prev) => Math.max(1, prev - 1))
                }
                className="go-back-btn"
              >
                Go Back
              </span>
            )}

            {currentScreen < 7 && (
              <span
                style={{
                  paddingTop: deviceType === "iOS" ? "25px" : "auto",
                  lineHeight: deviceType === "iOS" ? 0.8 : "auto",
                }}
                onClick={
                  !loadingSaveAndExit
                    ? handleSubmit(handleSaveAndExit)
                    : undefined
                }
              >
                {loadingSaveAndExit ? "Saving..." : "Save and Exit"}
              </span>
            )}

            <span
              style={{
                paddingTop: deviceType === "iOS" ? "25px" : "auto",
                lineHeight: deviceType === "iOS" ? 0.8 : "auto",
                opacity: currentScreen === 7 && !canSubmit() ? 0.5 : 1,
                cursor:
                  currentScreen === 7 && !canSubmit()
                    ? "not-allowed"
                    : "pointer",
              }}
              onClick={
                !loadingSaveAndContinue && (currentScreen !== 7 || canSubmit())
                  ? handleSubmit(handleNextStep)
                  : undefined
              }
            >
              {loadingSaveAndContinue
                ? "Submitting..."
                : currentScreen === 7
                  ? "Submit Application"
                  : "Save and Continue"}
            </span>
          </div>
        </form>

        {/* info for scholarship reviewers */}
        {currentScreen === 1 && (
          <div className="important-info-section">
            <IoMdInformationCircleOutline size={30} color="#333" />
            <p>
              If you are a scholarship reviewer, contact
              rho1914scholarships@gmail.com after submitting your first and last
              name.
            </p>
            <p>
              If you are a college or high school applicant, please continue
              with the application.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Account;
